import React, { useEffect, useState } from 'react';
import { MapPin, ShieldCheck, Percent, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const RegionsPage = () => {
  const { isAdmin } = useAuth();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/regions');
      setRegions(r.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const { id, name, code, default_currency, default_tax_rate, tax_label, active } = editing;
      await api.patch(`/regions/${id}`, { name, code, default_currency, default_tax_rate, tax_label, active });
      setEditing(null);
      await load();
    } catch (e) { /* */ }
    setSaving(false);
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl">
      <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] font-semibold uppercase text-emerald-300">
        <MapPin className="w-3.5 h-3.5" /> Regions
      </div>
      <h1 className="mt-2 text-3xl font-semibold text-white tracking-tight">Regions</h1>
      <p className="mt-1 text-white/55">Region defaults drive tax rules and T&amp;C templates. {isAdmin ? 'Admins can edit tax and currency defaults.' : 'Only admins can edit region defaults.'}</p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-white/10 text-left text-[10px] tracking-[0.2em] font-semibold uppercase text-white/50">
              <th className="px-6 py-4">Region</th>
              <th className="px-4 py-4">Code</th>
              <th className="px-4 py-4">Default currency</th>
              <th className="px-4 py-4">Tax</th>
              <th className="px-4 py-4">Status</th>
              {isAdmin && <th className="px-4 py-4 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isAdmin ? 6 : 5} className="px-6 py-8 text-center text-white/40">Loading…</td></tr>
            ) : regions.map((r) => (
              <tr key={r.id} className="border-b border-white/5 last:border-0">
                <td className="px-6 py-4 font-medium text-white">{r.name}</td>
                <td className="px-4 py-4">
                  <span className="font-mono text-[12px] text-cyan-300 bg-cyan-400/10 px-1.5 py-0.5 rounded">{r.code}</span>
                </td>
                <td className="px-4 py-4 text-white/75">{r.default_currency}</td>
                <td className="px-4 py-4 text-white/75">
                  <span className="inline-flex items-center gap-1.5">
                    <Percent className="w-3 h-3 text-purple-300" />
                    {(r.default_tax_rate * 100).toFixed(0)}% <span className="text-white/45">{r.tax_label}</span>
                  </span>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full border ${
                    r.active ? 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10' : 'text-white/40 border-white/15 bg-white/[0.03]'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${r.active ? 'bg-emerald-400' : 'bg-white/30'}`} />
                    {r.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                {isAdmin && (
                  <td className="px-4 py-4 text-right">
                    <button onClick={() => setEditing(r)} className="text-[13px] text-cyan-300 hover:text-cyan-200 font-semibold">Edit</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12121a] p-6">
            <div className="text-white font-semibold text-lg">Edit region</div>
            <div className="mt-5 grid grid-cols-2 gap-4">
              <label className="block col-span-2">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Name</span>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50" />
              </label>
              <label className="block">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Code</span>
                <input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50" />
              </label>
              <label className="block">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Default currency</span>
                <select value={editing.default_currency} onChange={(e) => setEditing({ ...editing, default_currency: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50">
                  {['USD', 'EUR', 'GBP', 'INR', 'DKK'].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Tax rate (%)</span>
                <input type="number" step="0.5" value={editing.default_tax_rate * 100} onChange={(e) => setEditing({ ...editing, default_tax_rate: parseFloat(e.target.value) / 100 })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50" />
              </label>
              <label className="block">
                <span className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Tax label</span>
                <input value={editing.tax_label} onChange={(e) => setEditing({ ...editing, tax_label: e.target.value })}
                  className="mt-2 w-full h-10 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white focus:outline-none focus:border-cyan-400/50" />
              </label>
              <label className="flex items-center gap-2 col-span-2 mt-2">
                <input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4" />
                <span className="text-[13px] text-white/70">Active</span>
              </label>
            </div>
            <div className="mt-6 flex items-center justify-end gap-3">
              <button onClick={() => setEditing(null)} className="h-10 px-5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-white/80 font-semibold text-sm">Cancel</button>
              <button onClick={saveEdit} disabled={saving} className="h-10 px-5 rounded-lg bg-emerald-400 hover:bg-emerald-300 disabled:opacity-60 text-black font-semibold text-sm inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionsPage;
